import * as React from 'react';
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup';
import {
    ID_FILTER_CAR_NUMBER,
    ID_FILTER_CARD_CUSTOMER,
    ID_FILTER_CARD_NUMBER,
    ID_FILTER_CATEGORY,
    ID_FILTER_INVOICE_DATE_FROM,
    ID_FILTER_INVOICE_DATE_TO,
    ID_FILTER_INVOICE_NUMBER,
    ID_FILTER_RA,
    ID_FILTER_REFUELLING_DATE_FROM,
    ID_FILTER_REFUELLING_DATE_TO,
    ID_FILTER_STATUS
} from "../constants/FuelFilters";
import { addFilter } from "../../../utils/FilterUtils";
import { useSetRecoilState } from 'recoil';
import { STATE_FUEL_ACTIVE_FILTERS } from '../constants/FuelCardRecoil';
import {
    NEW_CAR,
    NOT_DEFINED,
    RA_BILLED,
    RA_TO_BE_INVOICED,
    REJECTED,
    RENTAL,
    TO_BE_ANALYZED,
    WITHOUT_RA
} from '../constants/FuelsConstants';
import AdvancedFilter from "../../../component/AdvancedFilter";
import { Checkbox, ListItemText, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { asEndOfDay, asStartOfDay } from "../../../utils/FinanceDateUtils";
import { useFuelStatuses } from "../constants/useFuelStatuses";
import { useFuelCategories } from "../constants/useFuelCategories";

function FuelCardFilter() {
    const {t} = useTranslation('finance', {keyPrefix: 'fuel'});
    const setFilterDefs = useSetRecoilState(STATE_FUEL_ACTIVE_FILTERS);
    const {getStatus} = useFuelStatuses();
    const {getCategories} = useFuelCategories();

    const schema = Yup.object().shape({
        ra: Yup.string(),
        cardNumber: Yup.string(),
        status: Yup.array(),
        cardCustomer: Yup.string(),
        carNumber: Yup.string(),
        category: Yup.array(),
        invoiceNumber: Yup.string(),
        refuellingDateFrom: Yup.date().nullable().test("testRefuellingDateFrom", t('filters.errors.invalidDate'), (value, context) => {
            return !value || !context.parent.refuellingDateTo || (value <= context.parent.refuellingDateTo);
        }).typeError(t('filters.errors.invalidDate')),
        refuellingDateTo: Yup.date().nullable().test("testRefuellingDateTo", t('filters.errors.invalidDate'), (value, context) => {
            return !value || !context.parent.refuellingDateFrom || (value >= context.parent.refuellingDateFrom);
        }).typeError(t('filters.errors.invalidDate')),
        invoiceDateFrom: Yup.date().nullable().test("testInvoiceDateFrom", t('filters.errors.invalidDate'), (value, context) => {
            return !value || !context.parent.invoiceDateTo || (value <= context.parent.invoiceDateTo);
        }).typeError(t('filters.errors.invalidDate')),
        invoiceDateTo: Yup.date().nullable().test("testInvoiceDateTo", t('filters.errors.invalidDate'), (value, context) => {
            return !value || !context.parent.invoiceDateFrom || (value >= context.parent.invoiceDateFrom);
        }).typeError(t('filters.errors.invalidDate')),
    });

    const defaultValues = {
        ra: '',
        cardNumber: '',
        status: [] as Array<string>,
        cardCustomer: '',
        carNumber: '',
        category: [] as Array<string>,
        invoiceNumber: '',
        refuellingDateFrom: null,
        refuellingDateTo: null,
        invoiceDateFrom: null,
        invoiceDateTo: null
    }
    const renderRAField = (field: any) => (
        <TextField
            fullWidth
            value={field.value}
            onChange={field.onChange}
            label={t('filters.ra')}

        />
    );
    const renderCardNumberField = (field: any) => (
        <TextField
            fullWidth
            value={field.value}
            onChange={field.onChange}
            label={t('filters.cardNumber')}

        />
    );
    const renderStatusesField = (field: any, error: any, state: any, values: any) => (
        <TextField
            style={{width: "100%"}}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
            }}
            label={t('filters.' + ID_FILTER_STATUS)}
            value={field.value}
            onChange={field.onChange}
        >
            {statuses.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                    <ListItemText primary={status.label}/>
                </MenuItem>
            ))}
        </TextField>
    );
    const renderCardCustomerField = (field: any) => (
        <TextField
            fullWidth
            value={field.value}
            onChange={field.onChange}
            label={t('filters.cardCustomer')}

        />
    )
    const renderCarNumberField = (field: any) => (
        <TextField
            fullWidth
            value={field.value}
            onChange={field.onChange}
            label={t('filters.carNumber')}

        />
    )
    const renderCategoryField = (field: any, error: any, state: any, values: any) => (
        <TextField
            style={{width: "100%"}}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) =>
                    selected.map((s: any) =>
                        categories.find(ss => ss.value === s)!.label).join(', ')
            }}
            label={t('filters.' + ID_FILTER_CATEGORY)}
            value={field.value}
            onChange={field.onChange}
        >
            {categories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                    <Checkbox checked={field.value.indexOf(category.value) > -1}/>
                    <ListItemText primary={category.label}/>
                </MenuItem>
            ))}
        </TextField>
    )
    const renderRefuellingDateFromField = (field: any, error: any, state: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.' + ID_FILTER_REFUELLING_DATE_FROM)}
            value={field.value}
            maxDate={values['refuellingDateTo'] !== null ? values['refuellingDateTo'] : null}
            onChange={field.onChange}
            renderInput={(params) =>
                <TextField {...params} style={{width: "100%"}}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                           id={"refuellingDateFrom"}
                />}
            inputFormat={"L"}
        />
    )
    const renderRefuellingDateToField = (field: any, error: any, state: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.' + ID_FILTER_REFUELLING_DATE_TO)}
            value={field.value}
            minDate={values['refuellingDateFrom'] !== null ? values['refuellingDateFrom'] : null}
            onChange={field.onChange}
            renderInput={(params) =>
                <TextField {...params} style={{width: "100%"}}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                           id={"refuellingDateTo"}
                />}
            inputFormat={"L"}
        />
    )
    const renderInvoiceDateFromField = (field: any, error: any, state: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.' + ID_FILTER_INVOICE_DATE_FROM)}
            value={field.value}
            maxDate={values['invoiceDateTo'] !== null ? values['invoiceDateTo'] : null}
            onChange={field.onChange}
            renderInput={(params) =>
                <TextField {...params} style={{width: "100%"}}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                           id={"invoiceDateFrom"}
                />}
            inputFormat={"L"}
        />
    )
    const renderInvoiceDateToField = (field: any, error: any, state: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.' + ID_FILTER_INVOICE_DATE_TO)}
            value={field.value}
            minDate={values['invoiceDateFrom'] !== null ? values['invoiceDateFrom'] : null}
            onChange={field.onChange}
            renderInput={(params) =>
                <TextField {...params} style={{width: "100%"}}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                           id={"invoiceDateTo"}
                />}
            inputFormat={"L"}
        />
    )
    const renderInvoiceNumberField = (field: any) => (
        <TextField
            fullWidth
            value={field.value}
            onChange={field.onChange}
            label={t('filters.invoiceNumber')}

        />
    )

    const mainFiels = {
        ra: renderRAField,
        cardNumber: renderCardNumberField,
        status: renderStatusesField,
    };

    const fields = {
        cardCustomer: renderCardCustomerField,
        carNumber: renderCarNumberField,
        refuellingDateFrom: renderRefuellingDateFromField,
        refuellingDateTo: renderRefuellingDateToField,
        category: renderCategoryField,
        invoiceNumber: renderInvoiceNumberField,
        invoiceDateFrom: renderInvoiceDateFromField,
        invoiceDateTo: renderInvoiceDateToField,
    };

    const statuses = [
        {
            value: WITHOUT_RA,
            label: getStatus(WITHOUT_RA)
        },
        {
            value: TO_BE_ANALYZED,
            label: getStatus( TO_BE_ANALYZED)
        },
        {
            value: RA_TO_BE_INVOICED,
            label: getStatus( RA_TO_BE_INVOICED)
        },
        {
            value: RA_BILLED,
            label: getStatus(RA_BILLED)
        },
        {
            value: REJECTED,
            label: getStatus(REJECTED)
        },
    ];

    const categories = [
        {
            value: NOT_DEFINED,
            label: getCategories(NOT_DEFINED)
        },
        {
            value: RENTAL,
            label: getCategories(RENTAL)
        },
        {
            value: NEW_CAR,
            label: getCategories( NEW_CAR)
        }
    ];

    const submit = (data: any) => {
        addFilter(setFilterDefs, ID_FILTER_RA, data.ra);
        addFilter(setFilterDefs, ID_FILTER_CARD_NUMBER, data.cardNumber);
        addFilter(setFilterDefs, ID_FILTER_STATUS, data.status);
        addFilter(setFilterDefs, ID_FILTER_CARD_CUSTOMER, data.cardCustomer);
        addFilter(setFilterDefs, ID_FILTER_CAR_NUMBER, data.carNumber);
        addFilter(setFilterDefs, ID_FILTER_CATEGORY, data.category);
        addFilter(setFilterDefs, ID_FILTER_REFUELLING_DATE_FROM, asStartOfDay(data.refuellingDateFrom));
        addFilter(setFilterDefs, ID_FILTER_REFUELLING_DATE_TO, asEndOfDay(data.refuellingDateTo));
        addFilter(setFilterDefs, ID_FILTER_INVOICE_DATE_FROM, asStartOfDay(data.invoiceDateFrom));
        addFilter(setFilterDefs, ID_FILTER_INVOICE_DATE_TO, asEndOfDay(data.invoiceDateTo));
        addFilter(setFilterDefs, ID_FILTER_INVOICE_NUMBER, data.invoiceNumber);
    }

    const reset = () => {
        setFilterDefs([]);
    }

    return (
        <AdvancedFilter schema={schema} mainFields={mainFiels} fields={fields} defaultValues={defaultValues}
                        onSubmit={submit} onReset={reset}
        />
    )
}

export default FuelCardFilter;